import { type FormEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { fetchAssetTypesForBrowse } from '../api/getAssetById';
import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';
import { fetchProfile } from '../api/profileApi';
import { useAuth } from '../auth/AuthContext';
import { getSessionToken, getStoredLoginIdentifier } from '../auth/sessionToken';
import { GENERIC_PROFILE_AVATAR_SRC } from '../constants/homePageContent';
import { resolveOffizzSiteNavAssetTypes } from '../constants/filterableAssetTypes';
import { useHeaderDisplayName } from '../hooks/useHeaderDisplayName';
import { useUpcomingBookingNotifications } from '../hooks/useUpcomingBookingNotifications';
import { useI18n, translateUserTypeLabel } from '../i18n';
import { localizeAssetTypeLabel } from '../i18n/assetTypeLabels';
import { useAppNavigation, useAppView } from '../navigation/AppNavigationContext';
import { resolveProfileAvatarDisplay } from '../utils/profileAvatarDisplay';
import {
  loadLocalProfileAvatarSrc,
  PROFILE_AVATAR_UPDATED_EVENT,
} from '../utils/profileAvatarStorage';
import { extractProfileDisplaySummary } from '../utils/profileDisplaySummary';
import { NotificationsPanel } from './NotificationsPanel';
import { PLACY_ICON_SRC } from './OffizzBrandMark';
import { ProfileAvatarImage } from './ProfileAvatarImage';
import styles from './DesktopSiteBanner.module.css';

/**
 * Full-width website chrome for PC (≥768px). Mobile / fold keep {@link HomeHeader}.
 */
export function DesktopSiteBanner() {
  const { t, language, setLanguage } = useI18n();
  const view = useAppView();
  const { isLoggedIn, userLoginType, userName, logout } = useAuth();
  const {
    assetsBrowseParams,
    openAssets,
    openAuth,
    openProfile,
    openSearch,
    navigateFromBottomBar,
  } = useAppNavigation();
  const displayName = useHeaderDisplayName();
  const typeLabel = userLoginType ? translateUserTypeLabel(userLoginType, t) : null;
  const { upcoming, hasUpcoming, loadState, loadError, refresh } = useUpcomingBookingNotifications(
    isLoggedIn,
    userLoginType,
  );
  const [types, setTypes] = useState<GeneralInfoPicklistItem[]>([]);
  const [query, setQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsPanelOpen, setNotificationsPanelOpen] = useState(false);
  const [avatarSrc, setAvatarSrc] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const catalog = await fetchAssetTypesForBrowse();
        if (!cancelled) {
          setTypes(catalog);
        }
      } catch {
        if (!cancelled) {
          setTypes([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setAvatarSrc(null);
      return;
    }

    const applyLocal = () => {
      setAvatarSrc(resolveProfileAvatarDisplay(null, loadLocalProfileAvatarSrc()));
    };
    applyLocal();

    const onAvatarUpdated = () => {
      applyLocal();
    };
    window.addEventListener(PROFILE_AVATAR_UPDATED_EVENT, onAvatarUpdated);

    let cancelled = false;
    const loginId = getStoredLoginIdentifier();
    void (async () => {
      try {
        const data = await fetchProfile();
        if (cancelled) {
          return;
        }
        const summary = extractProfileDisplaySummary(
          data,
          userName,
          loginId,
          getSessionToken(),
        );
        setAvatarSrc(resolveProfileAvatarDisplay(summary.avatarSrc, loadLocalProfileAvatarSrc()));
      } catch {
        if (!cancelled) {
          applyLocal();
        }
      }
    })();

    return () => {
      cancelled = true;
      window.removeEventListener(PROFILE_AVATAR_UPDATED_EVENT, onAvatarUpdated);
    };
  }, [isLoggedIn, userName]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [menuOpen]);

  const navTypes = useMemo(() => resolveOffizzSiteNavAssetTypes(types), [types]);
  const hasProfilePhoto =
    Boolean(avatarSrc) && avatarSrc !== GENERIC_PROFILE_AVATAR_SRC;

  const goHome = useCallback(() => {
    navigateFromBottomBar('home');
  }, [navigateFromBottomBar]);

  const onSearchSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      const q = query.trim();
      openSearch(q.length > 0 ? q : undefined);
    },
    [openSearch, query],
  );

  const onLogout = useCallback(() => {
    setMenuOpen(false);
    logout();
    openAuth('login');
  }, [logout, openAuth]);

  const onOpenProfile = useCallback(() => {
    setMenuOpen(false);
    openProfile();
  }, [openProfile]);

  const onToggleLanguage = useCallback(() => {
    setLanguage(language === 'he' ? 'en' : 'he');
  }, [language, setLanguage]);

  return (
    <>
      <header className={styles.banner} role="banner">
        <div className={styles.inner}>
          <div className={styles.brandNav}>
            <button type="button" className={styles.logoBtn} onClick={goHome} aria-label="Placy">
              <span className={styles.logoDisc}>
                <img src={PLACY_ICON_SRC} alt="" className={styles.logoImg} draggable={false} />
              </span>
            </button>

            <nav className={styles.nav} aria-label={t('desktopBanner.navLabel')}>
              <button
                type="button"
                className={[styles.navLink, view === 'home' ? styles.navLinkActive : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={goHome}
              >
                {t('nav.home')}
              </button>
              {navTypes.map((type) => {
                const label = localizeAssetTypeLabel(type.label, language, t);
                const active =
                  view === 'assets' &&
                  assetsBrowseParams.catalogBrowse &&
                  assetsBrowseParams.assetTypeId === type.id;
                return (
                  <button
                    key={type.id}
                    type="button"
                    className={[styles.navLink, active ? styles.navLinkActive : '']
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() =>
                      openAssets({
                        assetTypeId: type.id,
                        assetTypeLabel: type.label,
                        catalogBrowse: true,
                        returnView: 'home',
                      })
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className={styles.userSlot}>
            {isLoggedIn ? (
              <div className={styles.userMenuWrap} ref={menuRef}>
                <button
                  type="button"
                  className={styles.userButton}
                  aria-label={displayName}
                  aria-expanded={menuOpen}
                  aria-controls={menuId}
                  aria-haspopup="menu"
                  onClick={() => setMenuOpen((open) => !open)}
                >
                  <span className={styles.userAvatar} aria-hidden>
                    {hasProfilePhoto && avatarSrc ? (
                      <ProfileAvatarImage
                        className={styles.userAvatarImg}
                        src={avatarSrc}
                        alt=""
                      />
                    ) : (
                      <span className={styles.userIconFallback}>
                        <UserGlyph />
                      </span>
                    )}
                  </span>
                  <span className={styles.userName}>{displayName}</span>
                </button>
                {menuOpen ? (
                  <div id={menuId} className={styles.userMenu} role="menu">
                    {typeLabel ? (
                      <p className={styles.userMenuMeta} role="presentation">
                        {typeLabel}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      className={styles.userMenuItem}
                      role="menuitem"
                      onClick={onOpenProfile}
                    >
                      {t('nav.profile')}
                    </button>
                    <button
                      type="button"
                      className={styles.userMenuItem}
                      role="menuitem"
                      onClick={onLogout}
                    >
                      {t('header.logOut')}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className={styles.authActions}>
                <button type="button" className={styles.authLink} onClick={() => openAuth('login')}>
                  {t('header.signIn')}
                </button>
                <span className={styles.authDivider} aria-hidden>
                  ·
                </span>
                <button
                  type="button"
                  className={styles.authLink}
                  onClick={() => openAuth('register')}
                >
                  {t('header.register')}
                </button>
              </div>
            )}
          </div>

          <form className={styles.searchForm} onSubmit={onSearchSubmit} role="search">
            <span className={styles.searchIcon} aria-hidden>
              <SearchGlyph />
            </span>
            <input
              className={styles.searchInput}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('desktopBanner.searchPlaceholder')}
              aria-label={t('header.searchTitle')}
            />
          </form>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => setNotificationsPanelOpen(true)}
              aria-label={t('header.notificationsTitle')}
            >
              <BellGlyph />
              {hasUpcoming ? <span className={styles.bellDot} aria-hidden /> : null}
            </button>

            <button
              type="button"
              className={styles.iconBtn}
              onClick={onToggleLanguage}
              aria-label={t('language.pickerLabel')}
              title={language === 'he' ? 'English' : 'עברית'}
            >
              <GlobeGlyph />
            </button>
          </div>
        </div>
      </header>
      {notificationsPanelOpen ? (
        <NotificationsPanel
          onClose={() => setNotificationsPanelOpen(false)}
          canLoadBookings={isLoggedIn}
          upcoming={upcoming}
          loadState={loadState}
          loadError={loadError}
          onRefresh={refresh}
        />
      ) : null}
    </>
  );
}

function UserGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
      <path
        fill="currentColor"
        d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8V22h19.2v-2.8c0-3.2-6.4-4.8-9.6-4.8z"
      />
    </svg>
  );
}

function SearchGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
      <path
        fill="currentColor"
        d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
      />
    </svg>
  );
}

function BellGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

function GlobeGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
      />
    </svg>
  );
}
